/**
 * 6-dropdown pre-offer verification checklist (R3 FIX K).
 *
 * Replaces the old "Storico recenti" panel in ClientePane.
 * Each select is a controlled component; change fires PATCH to
 * /api/offers/{id}/checklist with { field, value }.
 *
 * The green "X/6 verificate" chip counts how many fields have a value that
 * is NOT one of the "not yet verified" neutral options.
 */
import { useMemo, useState } from "react";
import axios from "axios";
import { CheckCircle2, Info } from "lucide-react";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

import { API } from "../../lib/backend";

const FIELDS = [
  {
    key: "prezzi_concorrenza",
    label: "Prezzi concorrenza verificati?",
    options: ["Non verificati", "Sì", "No", "Non disponibili"],
    neutral: "Non verificati",
  },
  {
    key: "giacenza",
    label: "Giacenza prodotti",
    options: [
      "Non verificata",
      "Tutti disponibili",
      "Parzialmente disponibili",
      "Non disponibili",
    ],
    neutral: "Non verificata",
  },
  {
    key: "resa_concordata",
    label: "Resa concordata",
    options: [
      "Non specificata",
      "Porto franco",
      "Franco fabbrica",
      "Loco cantiere",
    ],
    neutral: "Non specificata",
  },
  {
    key: "certificazioni",
    label: "Certificazioni richieste",
    options: [
      "Nessuna",
      "EN 10204 3.1",
      "ODA",
      "ATEX",
      "MOCA",
      "PED",
      "Altro",
    ],
    neutral: "Nessuna",
  },
  {
    key: "sconto_insoluti",
    label: "Cliente abituale: sconto/insoluti?",
    options: [
      "Non verificato",
      "Nessuno noto",
      "Sconto attivo",
      "Insoluti pendenti",
    ],
    neutral: "Non verificato",
  },
  {
    key: "approvazione_titolare",
    label: "Approvazione titolare > 3.000€",
    options: [
      "Non richiesta",
      "Da richiedere",
      "Ottenuta",
    ],
    neutral: "Non richiesta",
  },
];

export default function ChecklistVerificaOfferta({ offerId, initial }) {
  const [values, setValues] = useState(() => {
    const v = {};
    FIELDS.forEach((f) => {
      v[f.key] = (initial && initial[f.key]) || f.neutral;
    });
    return v;
  });
  const [saving, setSaving] = useState(null);

  const verifiedCount = useMemo(
    () =>
      FIELDS.filter((f) => values[f.key] && values[f.key] !== f.neutral).length,
    [values]
  );

  const setField = async (field, value) => {
    setValues((v) => ({ ...v, [field]: value }));
    if (!offerId) return;
    setSaving(field);
    try {
      await axios.patch(
        `${API}/offers/${offerId}/checklist`,
        { field, value },
        { withCredentials: true }
      );
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Errore salvataggio checklist"
      );
    } finally {
      setSaving(null);
    }
  };

  return (
    <div data-testid="checklist-verifica">
      <div className="flex items-center justify-between mb-2">
        <div className="tq-section-label">Checklist pre-offerta</div>
        <span
          className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full"
          style={{
            background: verifiedCount === FIELDS.length ? "#DCFCE7" : "#FEF3C7",
            color: verifiedCount === FIELDS.length ? "#166534" : "#92400E",
          }}
          data-testid="checklist-counter"
        >
          <CheckCircle2 className="h-3 w-3" />
          {verifiedCount}/{FIELDS.length} verificate
        </span>
      </div>
      <div className="space-y-1.5">
        {FIELDS.map((f) => (
          <div key={f.key} data-testid={`checklist-field-${f.key}`}>
            <div className="flex items-center gap-1 mb-0.5">
              <label
                className="text-[10px] uppercase tracking-wide flex-1"
                style={{ color: "var(--text-muted)" }}
              >
                {f.label}
              </label>
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="inline-flex cursor-help"
                      tabIndex={0}
                      data-testid={`checklist-tooltip-${f.key}`}
                    >
                      <Info
                        className="h-3 w-3"
                        style={{ color: "var(--text-muted)" }}
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent
                    className="max-w-[300px] text-[12px] leading-snug"
                    style={{ background: "#0f172a", color: "white" }}
                    side="left"
                    sideOffset={6}
                  >
                    {f.tooltip}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <select
              value={values[f.key]}
              onChange={(e) => setField(f.key, e.target.value)}
              className="w-full text-[12px] h-7 px-2 rounded bg-white"
              style={{ border: "1px solid var(--border-subtle)" }}
              data-testid={`checklist-select-${f.key}`}
              disabled={saving === f.key}
            >
              {f.options.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}
